import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface FollowUp {
  id: string;
  time: string;
  address: string;
  name?: string;
  source: 'running_list' | 'd2d_units';
  manual_reminder?: boolean;
  isHot?: boolean;
  isKT?: boolean;
}

export interface LeadCounts {
  hot: number;
  followup: number;
  na: number;
}

export interface ActiveArea {
  id: string;
  name: string;
  plz: string | null;
  is_active: boolean;
}

export function useDashboardData() {
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [todayFollowUps, setTodayFollowUps] = useState<FollowUp[]>([]);
  const [globalLeadCounts, setGlobalLeadCounts] = useState<LeadCounts>({
    hot: 0,
    followup: 0,
    na: 0
  });
  // Important: only show skeletons on the *initial* load (like RunningList)
  // to prevent flicker during realtime refreshes.
  const [isLoadingLeadCounts, setIsLoadingLeadCounts] = useState(true);
  const [isLoadingFollowUps, setIsLoadingFollowUps] = useState(false);
  const [activeArea, setActiveArea] = useState<ActiveArea | null>(null);
  const [isLoadingActiveArea, setIsLoadingActiveArea] = useState(false);

  // Debounce + initial-load tracking for lead counts (mirrors RunningList behavior)
  const leadCountsDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const leadCountsLoadedRef = useRef(false);

  // Update clock every second for accurate time display
  useEffect(() => {
    // Immediately set current time on mount
    setCurrentTime(new Date());
    
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Load global lead counts - with optional debounce for realtime updates
  const loadGlobalLeadCounts = useCallback(async (useDebounce = false) => {
    if (!user?.id) return;
    
    // Clear any pending debounced calls
    if (leadCountsDebounceRef.current) {
      clearTimeout(leadCountsDebounceRef.current);
      leadCountsDebounceRef.current = null;
    }

    // Only show loading skeletons before the first successful load
    const isInitialLoad = !leadCountsLoadedRef.current;
    if (isInitialLoad) setIsLoadingLeadCounts(true);

    const fetchData = async () => {
      try {
        const { data, error } = await supabase
          .from('d2d_units')
          .select(`
            id, tags, last_contact_at, next_reminder,
            d2d_houses!inner(
              id,
              d2d_streets!inner(
                id, is_active,
                d2d_areas!inner(id, is_active, created_by)
              )
            )
          `)
          .eq('user_id', user.id)
          .eq('d2d_houses.d2d_streets.is_active', true)
          .eq('d2d_houses.d2d_streets.d2d_areas.is_active', true)
          .eq('d2d_houses.d2d_streets.d2d_areas.created_by', user.id);

        if (error) throw error;

        const counts = {
          hot: (data || []).filter(unit =>
            unit.tags?.includes('HOT') ||
            (unit.tags?.includes('KI') && unit.last_contact_at &&
              new Date(unit.last_contact_at) > new Date(Date.now() - 24 * 60 * 60 * 1000))
          ).length,
          followup: (data || []).filter(unit => {
            if (unit.tags?.includes('WIN') || unit.tags?.includes('NA') || unit.tags?.includes('KI')) return false;
            if (unit.tags?.includes('KT') || unit.tags?.includes('HOT')) return true;
            if (!unit.next_reminder) return false;
            const reminderDate = new Date(unit.next_reminder);
            const today = new Date();
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            return reminderDate.toDateString() === today.toDateString() ||
              reminderDate.toDateString() === tomorrow.toDateString() ||
              reminderDate <= today;
          }).length,
          na: (data || []).filter(unit => unit.tags?.includes('NA')).length
        };

        setGlobalLeadCounts(counts);
        leadCountsLoadedRef.current = true;
      } catch (error) {
        console.error('Error loading global lead counts:', error);
      } finally {
        // Only flip off the skeleton state for the very first load.
        if (isInitialLoad) setIsLoadingLeadCounts(false);
      }
    };

    // Only debounce for realtime updates, NOT for initial load
    if (useDebounce && leadCountsLoadedRef.current) {
      leadCountsDebounceRef.current = setTimeout(fetchData, 300);
    } else {
      // Fetch immediately for initial load
      await fetchData();
    }
  }, [user?.id]);

  // Cleanup debounced lead-counts timer
  useEffect(() => {
    return () => {
      if (leadCountsDebounceRef.current) {
        clearTimeout(leadCountsDebounceRef.current);
      }
    };
  }, []);

  // Load today's follow-ups
  const loadTodayFollowUps = useCallback(async () => {
    if (!user?.id) return;
    setIsLoadingFollowUps(true);
    try {
      const today = new Date();
      const dayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const dayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

      const { data: runningListData } = await supabase
        .from('running_list_entries')
        .select('id, street, house_number, apartment_unit, follow_up_scheduled, notes')
        .eq('user_id', user.id)
        .gte('follow_up_scheduled', dayStart.toISOString())
        .lte('follow_up_scheduled', dayEnd.toISOString())
        .not('follow_up_scheduled', 'is', null);

      const { data: d2dUnitsData } = await supabase
        .from('d2d_units')
        .select(`
          id, 
          unit_name, 
          tags,
          last_contact_at,
          next_reminder, 
          follow_up_notes,
          manual_reminder,
          d2d_houses!inner(
            house_number,
            d2d_streets!inner(
              name,
              plz,
              d2d_areas!inner(
                id,
                is_active
              )
            )
          )
        `)
        .eq('user_id', user.id)
        .eq('d2d_houses.d2d_streets.d2d_areas.is_active', true)
        .gte('next_reminder', dayStart.toISOString())
        .lte('next_reminder', dayEnd.toISOString())
        .not('next_reminder', 'is', null);

      const followUps: FollowUp[] = [];

      if (runningListData) {
        runningListData.forEach(entry => {
          const time = new Date(entry.follow_up_scheduled).toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
          const address = `${entry.street} ${entry.house_number}`;
          const name = entry.apartment_unit || address;
          followUps.push({
            id: `rl_${entry.id}`,
            time,
            address,
            name,
            source: 'running_list',
            manual_reminder: true,
            isHot: false,
            isKT: false
          });
        });
      }

      if (d2dUnitsData) {
        d2dUnitsData.forEach(unit => {
          const time = new Date(unit.next_reminder).toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
          const address = `${unit.d2d_houses.d2d_streets.name} ${unit.d2d_houses.house_number}`;
          const name = unit.unit_name || address;
          const isHot = unit.tags?.includes('HOT') ||
            (unit.tags?.includes('KI') && unit.last_contact_at &&
              new Date(unit.last_contact_at) > new Date(Date.now() - 24 * 60 * 60 * 1000));
          const isKT = unit.tags?.includes('KT');

          followUps.push({
            id: `d2d_${unit.id}`,
            time,
            address,
            name,
            source: 'd2d_units',
            manual_reminder: unit.manual_reminder,
            isHot,
            isKT
          });
        });
      }

      followUps.sort((a, b) => a.time.localeCompare(b.time));
      setTodayFollowUps(followUps);
    } catch (error) {
      console.error('Error loading today\'s follow-ups:', error);
    } finally {
      setIsLoadingFollowUps(false);
    }
  }, [user?.id]);

  // Update overdue follow-ups then load today's follow-ups
  const updateOverdueAndLoadFollowUps = useCallback(async () => {
    if (!user) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        loadTodayFollowUps();
        return;
      }
      await supabase.functions.invoke('update-overdue-followups', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
    } catch (error) {
      console.error('Error updating overdue follow-ups:', error);
    } finally {
      loadTodayFollowUps();
    }
  }, [user, loadTodayFollowUps]);

  // Load active area
  const loadActiveArea = useCallback(async () => {
    if (!user?.id) return;
    setIsLoadingActiveArea(true);
    try {
      const { data, error } = await supabase
        .from('d2d_areas')
        .select('id, name, plz, is_active')
        .eq('created_by', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (error) throw error;
      setActiveArea(data);
    } catch (error) {
      console.error('Error loading active area:', error);
    } finally {
      setIsLoadingActiveArea(false);
    }
  }, [user?.id]);

  // Initial load
  useEffect(() => {
    if (user?.id) {
      loadGlobalLeadCounts();
      updateOverdueAndLoadFollowUps();
      loadActiveArea();
    }
  }, [user?.id, loadGlobalLeadCounts, updateOverdueAndLoadFollowUps, loadActiveArea]);

  // Realtime subscription for units changes
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('dashboard-units-realtime')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'd2d_units',
        filter: `user_id=eq.${user.id}`
      }, () => {
        // Use debounce for realtime updates to prevent rapid UI flickering
        loadGlobalLeadCounts(true);
        loadTodayFollowUps();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, loadGlobalLeadCounts, loadTodayFollowUps]);

  // Realtime subscription for areas changes
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('dashboard-areas-realtime')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'd2d_areas',
        filter: `created_by=eq.${user.id}`
      }, () => {
        loadActiveArea();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, loadActiveArea]);

  return {
    currentTime,
    todayFollowUps,
    globalLeadCounts,
    isLoadingLeadCounts,
    isLoadingFollowUps,
    activeArea,
    isLoadingActiveArea,
    loadGlobalLeadCounts,
    loadTodayFollowUps,
    updateOverdueAndLoadFollowUps,
    loadActiveArea
  };
}
